import axios from 'axios';

import { getFaro, setForceFullSampling } from '../services/telemetry';

/**
 * Drop-in replacement for the old `NewRelic()` helper (New Relic Browser
 * agent), now backed by Grafana Faro shipping to Alloy. Keeps the exact same
 * call surface — `noticeError` / `addPageAction` / `setUserId` /
 * `setCustomAttribute` — so every existing call site only needed an import
 * rename, not a rewrite.
 *
 * Faro's own `dedupe` + `ignoreErrors` config (see `services/telemetry.ts`)
 * already cover exact-duplicate and known-noisy-pattern error spam, so
 * `noticeError` doesn't need to re-implement either. What Faro *can't*
 * protect against is a single named event firing at high, sustained
 * frequency with different payloads each time (e.g. the old per-pong
 * `SocketLatency`) — that's fixed at the source (reduce the call frequency
 * where the event is fired), not here. `MAX_EVENTS_PER_MINUTE` below is only
 * a last-resort circuit breaker against a runaway bug, not a normal-usage
 * limit.
 */
const MAX_EVENTS_PER_MINUTE = 120;
const RATE_WINDOW_MS = 60_000;
let recentEventTimestamps: number[] = [];

const withinRateBudget = () => {
  const now = Date.now();
  recentEventTimestamps = recentEventTimestamps.filter(
    (t) => now - t < RATE_WINDOW_MS,
  );
  if (recentEventTimestamps.length >= MAX_EVENTS_PER_MINUTE) {
    return false;
  }
  recentEventTimestamps.push(now);
  return true;
};

/**
 * Axios's own error message ("Request failed with status code 404") never
 * includes which URL actually 404'd — every call site would otherwise need
 * to remember to attach it by hand. Extracted once here so every existing
 * `noticeError(axiosError)` call site gets it automatically, retroactively,
 * without touching each one.
 */
const axiosErrorContext = (
  error: unknown,
): Record<string, string> | undefined => {
  // axios 0.21's isAxiosError is `typeof payload === 'object' &&
  // payload.isAxiosError` — `typeof null` is 'object', so it throws on null
  // instead of returning false. That throw, from inside a component's
  // useEffect, took down the whole app via the ErrorBoundary.
  if (error == null || !axios.isAxiosError(error)) return undefined;
  return {
    requestUrl: error.config?.url ?? 'unknown',
    requestMethod: (error.config?.method ?? 'unknown').toUpperCase(),
    responseStatus: error.response?.status?.toString() ?? 'no response',
  };
};

const toStringAttributes = (
  attributes: Record<string, string | number | boolean> = {},
): Record<string, string> =>
  Object.fromEntries(
    Object.entries(attributes).map(([key, value]) => [key, String(value)]),
  );

interface TelemetryClient {
  addPageAction: (
    name: string,
    attributes?: Record<string, string | number | boolean>,
  ) => void;
  /**
   * `context` rides in Faro's own `context` field on the exception payload —
   * untouched by the stack-trace parser (which only extracts `at ...` frame
   * lines from `error.stack` and silently drops everything else). Use it for
   * an inner/original error's message+stack when wrapping: concatenating
   * that into the wrapper's own `.stack` string gets flattened together with
   * the wrapper's frames with no indication of where one stack ends and the
   * other begins, once Faro re-parses it.
   *
   * When `error` is an Axios error, `requestUrl` / `requestMethod` /
   * `responseStatus` are added automatically — Axios's own message ("Request
   * failed with status code 404") never says which URL, and that was by far
   * the single largest exception bucket in production. Explicit `context`
   * keys win over the automatic ones on collision.
   */
  noticeError: (error: unknown, context?: Record<string, string>) => void;
  setUserId: (uid: string | null) => void;
  setCustomAttribute: (
    name: string,
    value: string | number | boolean | null,
  ) => void;
  /**
   * Re-includes THIS session in Faro's export regardless of the global
   * `NEXT_PUBLIC_TELEMETRY_SAMPLING_RATE` (10% in production — see
   * deploy-production.yml). Faro's session instrumentation stamps every
   * session with an `isSampled` attribute at session-start time (random draw
   * against that rate) and its `beforeSend` hook silently drops any event
   * whose CURRENT session attributes aren't `isSampled: 'true'` — so a
   * session sampled out at page-load stays invisible for its entire
   * lifetime unless something flips that attribute back on.
   *
   * Call this the moment a session enters a flow you cannot afford to lose
   * to random sampling (e.g. student SSO, where losing 90% of failures would
   * make the funnel useless) — every event pushed AFTER this call ships,
   * regardless of the original sampling draw. Events already pushed before
   * the call are unaffected: the hook snapshots session attributes at push
   * time, not at send time.
   *
   * Routed through `telemetry.ts`'s `setForceFullSampling`, not just a
   * `setSession` attribute poke — see that flag's own comment for why a
   * plain attribute overwrite alone gets silently reverted.
   */
  forceFullSession: () => void;
}

/**
 * Use this when not in the react context.
 */
export const Telemetry = (): TelemetryClient | undefined => {
  const faro = getFaro();
  if (!faro) return undefined;

  return {
    addPageAction: (name, attributes) => {
      if (!withinRateBudget()) return;
      faro.api.pushEvent(name, toStringAttributes(attributes));
    },
    noticeError: (error, context) => {
      if (!withinRateBudget()) return;
      // Reporting an error must never itself throw — call sites run inside
      // effects and render paths, where a throw here becomes a
      // CriticalApplicationError that replaces the student's whole screen.
      try {
        const mergedContext = { ...axiosErrorContext(error), ...context };
        faro.api.pushError(
          error instanceof Error ? error : new Error(String(error)),
          Object.keys(mergedContext).length
            ? { context: mergedContext }
            : undefined,
        );
      } catch (reportingError) {
        // eslint-disable-next-line no-console
        console.error('Telemetry noticeError failed', reportingError);
      }
    },
    setUserId: (uid) => {
      if (uid) {
        faro.api.setUser({ id: uid });
      } else {
        faro.api.resetUser();
      }
    },
    setCustomAttribute: (name, value) => {
      // Must preserve the existing session's `id` (and anything else on
      // it) — Faro's SessionInstrumentation fires a `session_start` event
      // any time the session meta's `id` changes. Passing only
      // `{ attributes }` here (as an earlier version of this did) blanks
      // `id` out to `undefined` on every call, which reads as "a new
      // session" — gameAPI.ts calls this 4 times in a row on every single
      // join, so that bug meant 4 spurious session_start events per join.
      const currentSession = faro.api.getSession();
      faro.api.setSession({
        ...currentSession,
        attributes: {
          ...currentSession?.attributes,
          [name]: value === null ? '' : String(value),
        },
      });
    },
    forceFullSession: () => {
      setForceFullSampling();
      // `isSampled` is the exact attribute name Faro's own SessionInstrumentation
      // reads in its beforeSend hook — same merge shape as setCustomAttribute,
      // for the same session-id-preservation reason. Also nudges Faro's
      // metas listener to reconcile the session against storage right now,
      // rather than waiting for the next natural resample point — that
      // reconciliation is what actually applies the override above.
      const currentSession = faro.api.getSession();
      faro.api.setSession({
        ...currentSession,
        attributes: { ...currentSession?.attributes, isSampled: 'true' },
      });
    },
  };
};

/**
 * Use this in react components to send metrics.
 */
export const useTelemetry = () => {
  return {
    telemetry: Telemetry(),
  };
};
